/* eslint-disable react-hooks/exhaustive-deps */
import {
  ConfigApi,
  configApiRef,
  FetchApi,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FieldValidation } from '@rjsf/utils';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useTheme } from '@material-ui/core/styles';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useEffect, useRef, useState } from 'react';

/**
 * GitHubRepo_RepoNameStatus
 *
 * Companion field for the sibling `repoUrl` (RepoUrlPicker) in the Standardized
 * GitHub Repository Creation template. It collects no input of its own; instead
 * it performs a live, debounced availability check as the user types the
 * repository name — mirroring GitHub's "Checking availability…" →
 * "<name> is available" experience — and renders the status just below the
 * repository input. It also validates the name against the naming/format rules
 * (see `validateRepoName`) both live and on "Next". On step transition
 * ("Next"), `validateRepoNameStatus` hard-blocks progression when a naming rule
 * fails or the name already exists in the selected org.
 *
 * While mounted (first step only) it also applies two DOM-scoped tweaks to the
 * sibling RepoUrlPicker: it hides the fixed single-host control
 * (`useHideFixedRepoHost`) and replaces the built-in repository-name helper
 * text with Principal's naming-pattern guidance and a docs link that opens in a
 * new tab (`useCustomRepoNameHelp`).
 *
 * Keeping RepoUrlPicker as the input means the OAuth flow (USER_OAUTH_TOKEN)
 * and the `publish:github` step are unchanged.
 */

type RepoCoords = { owner: string; repo: string };

export function parseRepoUrl(repoUrl?: string): RepoCoords {
  if (!repoUrl) return { owner: '', repo: '' };
  try {
    const query = repoUrl.includes('?') ? repoUrl.split('?')[1] : repoUrl;
    const params = new URLSearchParams(query);
    return {
      owner: params.get('owner') ?? '',
      repo: params.get('repo') ?? '',
    };
  } catch {
    return { owner: '', repo: '' };
  }
}

// Assigned in the component via useApi so the standalone validator below can
// reach them (matches the existing GitHubTeams_TeamName field-extension pattern).
let configApi: ConfigApi;
let fetchApi: FetchApi;

// Repository-name naming & format rules, enforced BEFORE the async uniqueness
// check both live (inline, as the user types) and on "Next". Mirrors the rules
// documented in the Standardized GitHub Repository Creation template.
const MAX_REPO_NAME_LENGTH = 100;
// Allowed characters: letters, numbers, hyphen, underscore, period.
const ALLOWED_REPO_NAME = /^[A-Za-z0-9._-]+$/;
// Two or more consecutive special characters (e.g. --, __, .., -_).
const CONSECUTIVE_SPECIALS = /[._-]{2}/;

/**
 * Validate the repository name against the naming and format rules. Returns a
 * human-readable error message when invalid, or `undefined` when the name is
 * acceptable. Runs before (and independently of) the uniqueness check.
 */
export function validateRepoName(repo: string): string | undefined {
  if (!repo) return 'Repository name must not be empty.';
  if (repo.length > MAX_REPO_NAME_LENGTH) {
    return `Repository name must be between 1 and ${MAX_REPO_NAME_LENGTH} characters.`;
  }
  if (/\s/.test(repo)) return 'Repository name must not contain spaces.';
  if (!ALLOWED_REPO_NAME.test(repo)) {
    return 'Repository name may only contain letters (a-z, A-Z), numbers (0-9), hyphen (-), underscore (_) and period (.).';
  }
  if (repo.startsWith('.')) {
    return 'Repository name must not start with a period (.).';
  }
  if (repo.endsWith('.')) {
    return 'Repository name must not end with a period (.).';
  }
  if (repo.startsWith('-') || repo.endsWith('-')) {
    return 'Repository name must not start or end with a hyphen (-).';
  }
  if (CONSECUTIVE_SPECIALS.test(repo)) {
    return 'Repository name must not contain multiple consecutive special characters (e.g. my--repo, my__repo, my..repo).';
  }
  return undefined;
}

type CheckStatus = 'idle' | 'checking' | 'available' | 'taken' | 'error';

const GITHUB_GREEN = '#1a7f37';

const HOST_HIDE_STYLE_ID = 'gh-repo-hide-fixed-host';

// Default helper text Backstage's RepoUrlPicker renders under the repository
// name input (translation key `fields.repoUrlPicker.repository.description`).
// It is replaced in-place with Principal's naming-guidance help + docs link.
const DEFAULT_REPO_NAME_HELP = 'The name of the repository';

// Principal Engineering Handbook: application repository naming pattern.
const REPO_NAME_DOCS_URL =
  'https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/foundations/source-control/repos/#application-repository-naming-pattern';

// Custom help HTML injected in place of DEFAULT_REPO_NAME_HELP. The angle
// brackets in the format hint are HTML-escaped so they render literally, and
// the "Engineering handbook" link opens the docs in a new tab (rel=noopener
// guards against tab-nabbing). Only the anchor is colored (with the app theme's
// link color, passed in from the hook) so it looks like a normal Backstage
// link; the surrounding help text keeps its existing font and color. The
// content is fixed and trusted (only the theme color constant is interpolated),
// so assigning it via innerHTML is safe.
const buildRepoNameHelpHtml = (linkColor: string) =>
  'The name of the repository in the format ' +
  '&lt;app-name&gt;-&lt;qualifier&gt;-&lt;app-type&gt;. For more details refer ' +
  `<a href="${REPO_NAME_DOCS_URL}" target="_blank" rel="noopener noreferrer" ` +
  `style="color: ${linkColor}">Engineering handbook</a>.`;

/**
 * Replaces the built-in RepoUrlPicker repository-name helper text ("The name of
 * the repository") with Principal's naming-pattern guidance and a docs link
 * that opens in a new tab.
 *
 * Backstage renders that helper text from a hardcoded translation key inside
 * its own `RepoUrlPickerRepoName` component, so it cannot be overridden from the
 * template. Instead — mirroring `useHideFixedRepoHost` — this hook finds the
 * matching `FormHelperText` and rewrites its content, re-applying on every RJSF
 * re-render via a MutationObserver and restoring the original on unmount.
 *
 * The effect is strictly scoped to this template because `RepoNameStatus` is
 * mounted only while the template's first step is active.
 */
function useCustomRepoNameHelp() {
  // Color the injected "Engineering handbook" link with the app theme's link
  // color (Backstage adds `palette.link`; fall back to primary). Only the
  // anchor is colored — the surrounding help text is left untouched.
  const theme = useTheme();
  const linkColor =
    (theme.palette as { link?: string }).link ?? theme.palette.primary.main;

  useEffect(() => {
    // Marks helper elements we have rewritten so re-application is idempotent.
    const MARK_ATTR = 'data-gh-repo-help';
    // Remember each element's original markup so it can be restored on unmount.
    const original = new Map<HTMLElement, string>();
    // Help markup with the theme-colored anchor, rebuilt if the theme changes.
    const helpHtml = buildRepoNameHelpHtml(linkColor);

    // Normalize so the match tolerates trailing punctuation/whitespace that
    // some Backstage versions add to the translated string.
    const normalize = (text: string) =>
      text.trim().replace(/\.$/, '').toLowerCase();
    const target = normalize(DEFAULT_REPO_NAME_HELP);

    const apply = () => {
      // Match by class PREFIX, not `.MuiFormHelperText-root`: Backstage's seeded
      // JSS class-name generator suffixes MUI tokens (e.g.
      // `MuiFormHelperText-root-10613`), so an exact class selector never hits
      // (same reason `useHideFixedRepoHost` prefix-matches `MuiFormControl-marginNormal`).
      document
        .querySelectorAll<HTMLElement>('[class*="MuiFormHelperText-root"]')
        .forEach(el => {
          // Already rewritten and still intact (our docs link present): no-op.
          if (el.getAttribute(MARK_ATTR) === '1' && el.querySelector('a')) {
            return;
          }
          // Match the built-in helper by its (normalized) default text. This
          // also re-fires if a React re-render reset our element back to the
          // default text, so the custom help self-heals.
          if (normalize(el.textContent ?? '') === target) {
            if (!original.has(el)) {
              original.set(el, el.innerHTML);
            }
            el.innerHTML = helpHtml;
            el.setAttribute(MARK_ATTR, '1');
          }
        });
    };

    apply();
    const observer = new MutationObserver(() => apply());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      original.forEach((html, el) => {
        el.innerHTML = html;
        el.removeAttribute(MARK_ATTR);
      });
      original.clear();
    };
  }, [linkColor]);
}

/**
 * Visually hides the sibling RepoUrlPicker "Host" control when it is pinned to a
 * single host (github.com) and therefore offers no real choice.
 *
 * The host control is anchored on the stable `data-testid="host-select"`
 * attribute that `@backstage/plugin-scaffolder`'s `RepoUrlPickerHost` renders on
 * its `<Select>` (see RepoUrlPickerHost.esm.js). That host `<Select>` lives
 * inside RepoUrlPickerHost's own `<FormControl margin="normal">`, nested as:
 *
 *   FormControl (margin="normal")                 <- RepoUrlPickerHost, hide THIS
 *     Box (BackstageSelect-root)
 *       FormControl (BackstageSelect-formControl)  <- inner core-components wrapper
 *         InputLabel "Host"
 *         Select [data-testid="host-select"]
 *     FormHelperText "The host where…"
 *
 * We hide the CLOSEST `MuiFormControl-marginNormal` ancestor of the host select
 * — that is exactly RepoUrlPickerHost's control (label, dropdown AND helper) and
 * nothing else. We deliberately do NOT match `.MuiFormControl-root` or the RJSF
 * `.field-string` wrapper: the whole `repoUrl` RepoUrlPicker (Host + Owner +
 * Repository) shares those wrappers, so matching them collapses the ENTIRE
 * picker and leaves only Description. We also match by class PREFIX because
 * Backstage's seeded JSS class-name generator suffixes MUI tokens (e.g.
 * `MuiFormControl-marginNormal-10614`), so an exact class match never hits.
 *
 * Two complementary mechanisms are used so the host control collapses reliably:
 *   1. A global CSS `:has()` rule — declarative and self-healing across every
 *      RJSF re-render (supported by all current Chromium/WebKit/Firefox).
 *   2. An imperative MutationObserver fallback for engines without `:has()`,
 *      which walks up from the host `<Select>` to its closest
 *      `MuiFormControl-marginNormal` ancestor and collapses it.
 *
 * The effect is strictly scoped to this template because `RepoNameStatus` is
 * used exclusively by the Standardized GitHub Repository Creation template and
 * is mounted only while its first step is active; the injected style, the
 * observer and any inline style are all torn down on unmount.
 */
function useHideFixedRepoHost() {
  useEffect(() => {
    // 1) Declarative rule — hide ONLY the host's own margin="normal" FormControl.
    let styleEl = document.getElementById(
      HOST_HIDE_STYLE_ID,
    ) as HTMLStyleElement | null;
    const ownsStyle = !styleEl;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = HOST_HIDE_STYLE_ID;
      styleEl.textContent =
        '[class*="MuiFormControl-marginNormal"]:has([data-testid="host-select"]) { display: none !important; }';
      document.head.appendChild(styleEl);
    }

    // 2) Imperative fallback — collapse the CLOSEST margin="normal" FormControl
    //    wrapping the host select so its label, dropdown and helper text
    //    disappear together, leaving Owner/Repository untouched.
    const hidden = new Set<HTMLElement>();
    const hide = () => {
      document
        .querySelectorAll<HTMLElement>('[data-testid="host-select"]')
        .forEach(select => {
          // Collapse ONLY RepoUrlPickerHost's own `<FormControl margin="normal">`
          // (label, dropdown AND helper). We match the class by PREFIX because
          // Backstage's seeded JSS class-name generator suffixes MUI tokens
          // (e.g. `MuiFormControl-marginNormal-10614`), so an exact match never
          // hits. We stop at the CLOSEST such wrapper: the whole `repoUrl`
          // RepoUrlPicker (Host + Owner + Repository) shares the outer
          // `.MuiFormControl-root`/`.field-string` wrappers, so matching those
          // would hide the entire picker.
          let target: HTMLElement | null = null;
          let node: HTMLElement | null = select;
          while (node) {
            const classes = node.classList;
            if (
              classes &&
              Array.from(classes).some(c =>
                c.startsWith('MuiFormControl-marginNormal'),
              )
            ) {
              target = node;
              break;
            }
            node = node.parentElement;
          }
          if (target && target.style.display !== 'none') {
            target.style.display = 'none';
            hidden.add(target);
          }
        });
    };

    hide();
    const observer = new MutationObserver(() => hide());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      hidden.forEach(control => {
        control.style.display = '';
      });
      hidden.clear();
      if (ownsStyle) {
        styleEl?.remove();
      }
    };
  }, []);
}

export const RepoNameStatus = (props: FieldExtensionComponentProps<string>) => {
  const { rawErrors, onChange, formContext } = props;

  // Scope the host-hiding rule to this template by tying it to this field's
  // mount lifecycle. Must run on every render, so it is called before any early
  // return (Rules of Hooks).
  useHideFixedRepoHost();

  // Replace Backstage's built-in "The name of the repository" helper text with
  // Principal's naming-pattern guidance + docs link (opens in a new tab). Also
  // scoped to this template via this field's mount lifecycle.
  useCustomRepoNameHelp();

  // Wire the module-level apis for the validator below. These hooks must run on
  // every render, so they are called before any early return.
  fetchApi = useApi(fetchApiRef);
  configApi = useApi(configApiRef);

  // Backstage's `createAsyncValidators` only runs a field's validation when the
  // field is PRESENT in formData (it iterates `Object.entries(formData)`).
  // Because this companion field collects no user input, without a value it
  // would never appear in formData and `validateRepoNameStatus` would never
  // fire on "Next". Mirror the sibling RepoUrlPicker coordinates into this
  // field's own value so the on-"Next" check is guaranteed to run.
  const repoUrl = (formContext?.formData as { repoUrl?: string } | undefined)
    ?.repoUrl;
  const { owner, repo } = parseRepoUrl(repoUrl);
  const mirrored = owner && repo ? `${owner}/${repo}` : undefined;
  const nameError = repo ? validateRepoName(repo) : undefined;

  useEffect(() => {
    onChange(mirrored);
  }, [mirrored]);

  const [status, setStatus] = useState<CheckStatus>('idle');
  // Guards against out-of-order responses while the user keeps typing.
  const latestKey = useRef('');

  // Live, debounced availability check. Re-runs whenever the org or repository
  // name changes and reflects the result inline, GitHub-style.
  useEffect(() => {
    const key = mirrored ?? '';
    latestKey.current = key;

    if (!owner || !repo || nameError || !fetchApi || !configApi) {
      setStatus('idle');
      return undefined;
    }

    setStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const baseUrl = configApi.getString('backend.baseUrl');
        const res = await fetchApi.fetch(
          `${baseUrl}/api/validation/github-repo`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ org: owner, repo }),
          },
        );
        if (latestKey.current !== key) return;
        if (!res.ok) {
          setStatus('error');
          return;
        }
        const data = (await res.json()) as { exists: boolean };
        if (latestKey.current !== key) return;
        setStatus(data.exists ? 'taken' : 'available');
      } catch {
        if (latestKey.current === key) setStatus('error');
      }
    }, 500);

    return () => clearTimeout(handle);
  }, [owner, repo, mirrored, nameError]);

  // Once "Next" has produced a blocking error for this field, defer to the
  // native field error so the same message is never shown twice.
  if (rawErrors && rawErrors.length > 0) {
    return null;
  }

  if (repo && nameError) {
    return (
      <Box display="flex" alignItems="center" gridGap={4} mt={0.25}>
        <ErrorOutlineIcon fontSize="small" color="error" />
        <Typography variant="body2" color="error">
          {nameError}
        </Typography>
      </Box>
    );
  }

  if (status === 'checking') {
    return (
      <Box display="flex" alignItems="center" gridGap={8} mt={0.25}>
        <CircularProgress size={12} />
        <Typography variant="body2" color="textSecondary">
          Checking availability…
        </Typography>
      </Box>
    );
  }

  if (status === 'available') {
    return (
      <Box display="flex" alignItems="center" gridGap={4} mt={0.25}>
        <CheckCircleOutlineIcon
          fontSize="small"
          style={{ color: GITHUB_GREEN }}
        />
        <Typography variant="body2" style={{ color: GITHUB_GREEN }}>
          {repo} is available
        </Typography>
      </Box>
    );
  }

  if (status === 'taken') {
    return (
      <Box display="flex" alignItems="center" gridGap={4} mt={0.25}>
        <ErrorOutlineIcon fontSize="small" color="error" />
        <Typography variant="body2" color="error">
          Repository '{repo}' already exists in the {owner} organization. Please
          choose a different name.
        </Typography>
      </Box>
    );
  }

  return null;
};

/**
 * Async field validation — runs on step transition ("Next"). Re-derives the
 * repo coordinates from `repoUrl`, first enforces the naming/format rules and
 * then hard-blocks progress if the name is already taken in the selected
 * organization.
 */
export const validateRepoNameStatus = async (
  _value: string,
  validation: FieldValidation,
  { formData }: { formData?: { repoUrl?: string } },
) => {
  const { owner, repo } = parseRepoUrl(formData?.repoUrl);
  if (!owner || !repo) return;

  const nameError = validateRepoName(repo);
  if (nameError) {
    validation.addError(nameError);
    return;
  }

  if (!fetchApi || !configApi) return;

  try {
    const baseUrl = configApi.getString('backend.baseUrl');
    const res = await fetchApi.fetch(`${baseUrl}/api/validation/github-repo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ org: owner, repo }),
    });
    if (!res.ok) return;
    const data = (await res.json()) as { exists: boolean };
    if (data.exists) {
      validation.addError(
        `Repository '${repo}' already exists in the ${owner} organization. Please choose a different name.`,
      );
    }
  } catch {
    // Network/backend errors are non-blocking here; the server-side
    // `pfg:validate:github-repo` step is the authoritative guard.
  }
};

// GitHubRepo_CopyCodefiles: optional field to seed the new repo with an existing
// repo's files. The pasted URL is checked live as the user types — a green tick
// when the source repo exists, otherwise "not found" — and clears on every edit.
// Existence is also enforced server-side by Repo Foundry, which clones with its
// own GitHub App and rolls back if the source can't be read.

const COPY_NOT_FOUND_MESSAGE =
  'That repository could not be found, please check the URL.';

/**
 * Parse a pasted GitHub repository reference into `{ owner, repo }`. Accepts
 * `https://github.com/<owner>/<repo>`, `git@github.com:<owner>/<repo>.git`, a
 * bare `<owner>/<repo>`, and URLs with extra path segments (e.g. `/tree/main`).
 * Returns empty strings when it cannot be parsed to a valid owner/repo pair.
 */
export function parseGithubRepoUrl(input?: string): {
  owner: string;
  repo: string;
} {
  if (!input) return { owner: '', repo: '' };
  let s = input.trim();
  if (!s) return { owner: '', repo: '' };

  s = s.replace(/^git@github\.com:/i, '');
  s = s.replace(/^https?:\/\/(?:www\.)?github\.com\//i, '');
  s = s.replace(/^github\.com\//i, '');
  s = s.replace(/^\/+/, '');

  const segs = s.split('/').filter(Boolean);
  if (segs.length < 2) return { owner: '', repo: '' };

  const owner = segs[0];
  const repo = segs[1].replace(/\.git$/i, '');
  if (!/^[A-Za-z0-9-]+$/.test(owner)) return { owner: '', repo: '' };
  if (!/^[A-Za-z0-9._-]{1,100}$/.test(repo)) return { owner: '', repo: '' };
  return { owner, repo };
}

// Existence check for the copy-from source, used by the live field. Answered by
// Repo Foundry's /source-check endpoint, which uses the same GitHub App token
// that actually clones the source — so a repo the copy worker can read is never
// reported missing (unlike the PDP-app-scoped repo-name route, which 404s on
// cross-org sources it is not installed on). Returns true/false when known, or
// undefined when the check could not be completed (treated as non-blocking).
async function checkCopySourceExists(
  owner: string,
  repo: string,
): Promise<boolean | undefined> {
  if (!fetchApi || !configApi) return undefined;
  try {
    const baseUrl = configApi.getString('backend.baseUrl');
    const res = await fetchApi.fetch(
      `${baseUrl}/api/repo-foundry/source-check?org=${encodeURIComponent(
        owner,
      )}&repo=${encodeURIComponent(repo)}`,
      { headers: { Accept: 'application/json' } },
    );
    // 400 means the org/repo is not accessible to Repo Foundry (bad org or the
    // App is not installed) — treat that as "not found". 5xx/network errors stay
    // non-blocking so a transient outage never shows a false negative.
    if (res.status === 400) return false;
    if (!res.ok) return undefined;
    const data = (await res.json()) as { exists: boolean };
    return data.exists;
  } catch {
    return undefined;
  }
}

/**
 * Async field validation — runs on "Next". The copy-from source is optional, so
 * a blank value passes. Any non-blank value must resolve to a real repository
 * that Repo Foundry can read; anything else (a malformed URL, an unknown
 * org/repo, or a source the App cannot access) blocks progression with the same
 * "not found" message shown live under the field.
 */
export const validateCopyCodefiles = async (
  value: string,
  validation: FieldValidation,
) => {
  const raw = (value ?? '').trim();
  if (!raw) return;

  const { owner, repo } = parseGithubRepoUrl(raw);
  if (!owner || !repo) {
    validation.addError(COPY_NOT_FOUND_MESSAGE);
    return;
  }

  const exists = await checkCopySourceExists(owner, repo);
  // Only a confirmed miss blocks Next. An unknown result (network/5xx, returned
  // as undefined) stays non-blocking so a transient outage never fails a valid
  // source.
  if (exists === false) {
    validation.addError(COPY_NOT_FOUND_MESSAGE);
  }
};

type CopyCheckStatus = 'idle' | 'checking' | 'found' | 'notfound' | 'unknown';

export const CopyCodefilesField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, rawErrors } = props;
  fetchApi = useApi(fetchApiRef);
  configApi = useApi(configApiRef);

  const [status, setStatus] = useState<CopyCheckStatus>('idle');
  // Guards against out-of-order responses while the user keeps typing.
  const latestKey = useRef('');

  const value = formData ?? '';

  // Live, debounced existence check: stay silent until the value parses to an
  // owner/repo, then show a green tick when the source exists or "not found"
  // otherwise. Re-runs on every edit so the status clears as the URL changes.
  useEffect(() => {
    const key = value.trim();
    latestKey.current = key;

    // Empty is allowed (the field is optional) — stay silent.
    if (!key) {
      setStatus('idle');
      return undefined;
    }

    // A non-empty value that can't be parsed into an owner/repo is treated as
    // "not found" so any malformed URL is flagged (and blocked on Next).
    const { owner, repo } = parseGithubRepoUrl(key);
    if (!owner || !repo) {
      setStatus('notfound');
      return undefined;
    }

    setStatus('checking');
    const handle = setTimeout(async () => {
      const exists = await checkCopySourceExists(owner, repo);
      if (latestKey.current !== key) return;
      // Confirmed match → found, confirmed miss → notfound, and an unavailable
      // check (undefined) stays neutral so a transient outage never shows a
      // false negative.
      let next: CopyCheckStatus = 'unknown';
      if (exists === true) next = 'found';
      else if (exists === false) next = 'notfound';
      setStatus(next);
    }, 500);

    return () => clearTimeout(handle);
  }, [value]);

  const hasRawError = Boolean(rawErrors && rawErrors.length > 0);
  // Live status wins over a stale "Next" error: once the URL resolves to a real
  // repo we show the green tick even if RJSF still holds the previous rawError
  // (it clears on the next step transition, which now passes).
  const showChecking = status === 'checking';
  const showFound = status === 'found';
  // An unknown live result stays neutral: don't let a stale "Next" rawError flip
  // it back to "not found" for a source we could not verify.
  const showNotFound =
    !showFound &&
    !showChecking &&
    status !== 'unknown' &&
    (status === 'notfound' || hasRawError);

  return (
    <>
      <TextField
        id={props.idSchema?.$id}
        fullWidth
        label="Add code files from an existing repository (optional)"
        placeholder="https://github.com/<org>/<repo>"
        value={formData ?? ''}
        onChange={event => onChange(event.target.value || undefined)}
        error={showNotFound}
        helperText="Optionally enter a Principal-owned repository URL to copy its files into the new repo."
      />
      {showChecking && (
        <Box display="flex" alignItems="center" gridGap={8} mt={0.5}>
          <CircularProgress size={12} />
          <Typography variant="body2" color="textSecondary">
            Checking repository…
          </Typography>
        </Box>
      )}
      {showFound && (
        <Box display="flex" alignItems="center" gridGap={4} mt={0.5}>
          <CheckCircleOutlineIcon
            fontSize="small"
            style={{ color: GITHUB_GREEN }}
          />
          <Typography variant="body2" style={{ color: GITHUB_GREEN }}>
            Repository found.
          </Typography>
        </Box>
      )}
      {showNotFound && (
        <Box display="flex" alignItems="center" gridGap={4} mt={0.5}>
          <ErrorOutlineIcon fontSize="small" color="error" />
          <Typography variant="body2" color="error">
            {COPY_NOT_FOUND_MESSAGE}
          </Typography>
        </Box>
      )}
    </>
  );
};
